/**
 * Bloco ACCOUNT-DATA do OrdersRouter — extraído de ordersRouter.jsx na
 * modularização dos god-objects. Contrato void-and-push do backend:
 * `getAccountDataAsync` apenas INVOCA "getAccountData" no hub; o dado chega
 * via push em `receiveAccountData`. Cobre: fetch-throttle 2s pós-report,
 * gate offline/manualLogout, dedup por string, delta-registration dos
 * símbolos de posição no marketdata (evita flicker), notify-throttle 500ms
 * com coalescing (entrega o ÚLTIMO pendente) e diagnóstico de slow-timeout
 * (wake-up throttling do browser).
 *
 * Corpos preservados byte-a-byte, `this` → `rt`; estado permanece NA
 * INSTÂNCIA Router; chamadas internas via `rt.<método>` (delegates).
 * Contrato pinado ANTES da extração: routerAccountDataContract.test.js.
 */
import marketdata from "./marketdata.jsx";
import logger from "../../utils/logger";
import timeoutManager from "../../utils/timeoutManager.js";
import performanceMonitor from "../../utils/performanceMonitor.js";

export const getAccountDataAsync = async (rt) => {
  try {
    if (import.meta.env.DEV) {
      console.log(
        "🔍 getAccountDataAsync called from:",
        new Error().stack?.split("\n")[2]?.trim(),
      );
    }
    return await rt.signalRConnection.invoke("getAccountData");
  } catch (err) {
    console.error(err.toString());
    return null;
  }
};

// Throttled version for use after reports - prevents excessive server calls
export const scheduleAccountDataFetch = (rt) => {
  const now = Date.now();
  const timeSinceLastFetch = now - rt.lastAccountDataFetchTime;

  // If enough time has passed, fetch immediately
  if (timeSinceLastFetch >= rt.ACCOUNT_DATA_THROTTLE_MS) {
    rt.lastAccountDataFetchTime = now;
    rt.getAccountDataAsync();
    return;
  }

  // Otherwise, schedule a fetch if not already pending
  if (!rt.pendingAccountDataFetch) {
    rt.pendingAccountDataFetch = true;
    const delay = rt.ACCOUNT_DATA_THROTTLE_MS - timeSinceLastFetch;

    rt.accountDataFetchTimeout = timeoutManager.setTimeout(() => {
      rt.pendingAccountDataFetch = false;
      rt.accountDataFetchTimeout = null;
      rt.lastAccountDataFetchTime = Date.now();
      rt.getAccountDataAsync();
    }, delay);
  }
};

// validateAccountData = (accdata) => {
//     if (!accdata || typeof accdata !== 'object') {
//         console.warn('Invalid account data received:', accdata);
//         return false;
//     }

//     // Check for required fields
//     const requiredFields = ['balances', 'positions', 'openOrders'];
//     for (const field of requiredFields) {
//         if (!(field in accdata)) {
//             console.warn(`Missing required field in account data: ${field}`);
//             return false;
//         }
//     }

//     // Validate balances structure - can be object or array
//     if (typeof accdata.balances !== 'object' && !Array.isArray(accdata.balances)) {
//         console.warn('Balances should be an object or array');
//         return false;
//     }

//     // Validate positions structure
//     if (typeof accdata.positions !== 'object') {
//         console.warn('Positions should be an object');
//         return false;
//     }

//     // Validate openOrders structure
//     if (typeof accdata.openOrders !== 'object') {
//         console.warn('OpenOrders should be an object');
//         return false;
//     }

//     return true;
// }

export const receiveAccountData = (rt, dataString) => {
  // Track for performance monitor
  performanceMonitor.trackAccountData(false);
  rt._diag.accountDataReceived++;

  // Ignore account data if router is offline or manually logged out
  if (rt.state === "offline" || rt.manualLogout) {
    console.log("📊 AccountData: Ignored (offline or manual logout)");
    return;
  }

  try {
    var accdata = JSON.parse(dataString);

    // Quick change detection - skip if data is identical to last received
    if (rt._lastAccountDataString && dataString === rt._lastAccountDataString) {
      performanceMonitor.trackAccountData(true); // Track as skipped
      rt._diag.dedupHits++;
      console.log("📊 AccountData: Skipped (identical to previous)");
      return; // Identical data, skip processing
    }
    rt._diag.dedupMisses++;
    rt._lastAccountDataString = dataString;

    // DEBUG: Dump full account data received from router
    console.log("📊 AccountData RECEIVED FROM ROUTER:", {
      balances: accdata.balances,
      positions: accdata.positions,
      openOrders: accdata.openOrders,
      openOrdersCount: accdata.openOrders
        ? Object.keys(accdata.openOrders).reduce(
            (sum, sym) => sum + (accdata.openOrders[sym]?.length || 0),
            0,
          )
        : 0,
      trades: accdata.trades?.length || 0,
    });

    // Keep a reference
    rt.accountData = accdata;

    // Delta-based quote registration for position symbols
    // Only register NEW symbols and unregister REMOVED symbols (avoids flicker)
    if (rt.accountData && rt.accountData.positions) {
      try {
        const newPositionSymbols = new Set(
          Object.keys(rt.accountData.positions),
        );

        // Unregister symbols no longer in positions
        for (const symbol of rt._registeredPositionSymbols) {
          if (!newPositionSymbols.has(symbol)) {
            marketdata.unregisterQuote(symbol, "positions");
          }
        }

        // Register new symbols in positions
        for (const symbol of newPositionSymbols) {
          if (!rt._registeredPositionSymbols.has(symbol)) {
            marketdata.registerQuote(symbol, "positions");
          }
        }

        rt._registeredPositionSymbols = newPositionSymbols;
      } catch (e) {
        console.warn("Position quote registration failed:", e);
      }
    }

    // Fire update - use immediate mode if flag is set (after order placement)
    const forceImmediate = rt._forceImmediateAccountData;
    rt._forceImmediateAccountData = false; // Reset flag
    rt.fireUpdateAccountData(accdata, forceImmediate);
  } catch (error) {
    console.error("Error processing account data:", error);
  }
};

export const clearAccountData = (rt) => {
  let ad = {
    balances: [],
    openOrders: [],
    trades: [],
    positions: [],
  };
  rt.accountData = ad;
  // Use immediate=true for critical clear operation
  rt.fireUpdateAccountData(ad, true);
};
// Internal method to actually notify receivers
export const _notifyAccountDataReceivers = (rt, accountData) => {
  if (rt.receivers.length > 0) {
    var r = rt.receivers.slice(0);
    for (var i = 0; i < r.length; i++) {
      if (r[i].receiveAccountData) {
        r[i].receiveAccountData(accountData);
      }
    }
  }
};

// Throttled notification to prevent rapid-fire updates to receivers
// Set immediate=true for critical updates (e.g., clearAccountData, logout)
export const fireUpdateAccountData = (rt, accountData, immediate = false) => {
  const now = Date.now();
  const timeSinceLastNotify = now - rt.lastAccountDataNotifyTime;

  // For immediate updates or if enough time has passed, notify now
  if (immediate || timeSinceLastNotify >= rt.ACCOUNT_DATA_NOTIFY_THROTTLE_MS) {
    rt.lastAccountDataNotifyTime = now;
    rt.pendingAccountData = null;
    rt.pendingAccountDataNotify = false;
    if (rt.accountDataNotifyTimeout) {
      clearTimeout(rt.accountDataNotifyTimeout);
      rt.accountDataNotifyTimeout = null;
    }
    rt._diag.throttleImmediate++;
    rt._notifyAccountDataReceivers(accountData);
    return;
  }

  // Otherwise, schedule a notification with the latest data
  rt.pendingAccountData = accountData;
  if (!rt.pendingAccountDataNotify) {
    rt.pendingAccountDataNotify = true;
    rt._diag.throttleScheduled++;
    const delay = rt.ACCOUNT_DATA_NOTIFY_THROTTLE_MS - timeSinceLastNotify;
    const scheduledAt = Date.now();

    rt.accountDataNotifyTimeout = timeoutManager.setTimeout(() => {
      const actualDelay = Date.now() - scheduledAt;
      if (actualDelay > rt._diag.throttleMaxActualDelayMs) {
        rt._diag.throttleMaxActualDelayMs = actualDelay;
      }
      // Smoking gun for browser wake-up throttling: scheduled 500ms but
      // browser delivered much later. Emit a single warning per slow
      // timeout — volume is naturally bounded by how often this fires.
      if (actualDelay > delay * 2 && actualDelay > 1000) {
        rt._diag.throttleSlowTimeoutCount++;
        try {
          logger.diagnostic.warning("[OrdersThrottle] slow-timeout", {
            scheduledDelayMs: delay,
            actualDelayMs: actualDelay,
            ratio: Number((actualDelay / delay).toFixed(2)),
            documentHidden:
              typeof document !== "undefined" ? document.hidden : null,
            documentHasFocus:
              typeof document !== "undefined" &&
              typeof document.hasFocus === "function"
                ? document.hasFocus()
                : null,
            pendingPositionsCount: rt.pendingAccountData?.positions
              ? Object.keys(rt.pendingAccountData.positions).length
              : 0,
          });
        } catch (e) {
          console.warn("[OrdersThrottle] log emit failed", e);
        }
      }
      rt._diag.throttleTimeoutFired++;
      rt.accountDataNotifyTimeout = null;
      rt.pendingAccountDataNotify = false;
      rt.lastAccountDataNotifyTime = Date.now();
      if (rt.pendingAccountData) {
        rt._notifyAccountDataReceivers(rt.pendingAccountData);
        rt.pendingAccountData = null;
      }
    }, delay);
  } else {
    rt._diag.throttleAccumulated++;
  }
};
